"use strict";

/**
 * @classdesc
 * Abstrakte Wrapperklasse für Kartenobjekte.
 *
 * @class
 * @abstract
 * @extends gc.gis.GCWrappable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @template L
 *
 * @param {gc.gis.map.GCMapView} holder Übergeordnete Kartenwrapperinstanz
 * @param {Object} options Aufrufoptionen (siehe Implementierungen)
 */
gc.gis.map.GCAbstractMap = function( holder, options )
{
	gc.gis.GCWrappable.call( this );

	/**
	 * @protected
	 * @type {gc.gis.map.GCMapView}
     */
	this._holder = holder;
};
gc.gis.map.GCAbstractMap.prototype = Object.create( gc.gis.GCWrappable.prototype );

/**
 * Gibt die übergeordnete Kartenwrapperinstanz zurück.
 *
 * @public
 * @method
 * @return {gc.gis.map.GCMapView}
 */
gc.gis.map.GCAbstractMap.prototype.getMapView = function()
{
	return this._holder;
};

/**
 * Liefert eine Factory zur Layer-Erzeugung in Abhängigkeit der eingesetzten Kartenbibliothek.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.map.AbstractLayerFactory}
 */
gc.gis.map.GCAbstractMap.getLayerFactory = gc.gis.abstractMethod;

/**
 * Fügt einen Layer der Karte hinzu.
 *
 * @public
 * @abstract
 * @method
 * @param {L} layer Hinzuzufügende Layerinstanz der Kartenbibliothek
 */
gc.gis.map.GCAbstractMap.prototype.addLayer = gc.gis.abstractMethod;

/**
 * Fügt einen Layer der Karte an einem bestimmten Index hinzu.
 *
 * @public
 * @abstract
 * @method
 * @param {L} layer Hinzuzufügende Layerinstanz der Kartenbibliothek
 * @param {number} index Zielindex
 */
gc.gis.map.GCAbstractMap.prototype.insertLayerAt = gc.gis.abstractMethod;

/**
 * Entfernt einen Layer aus der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {L} layer Zu entfernende Layerinstanz der Kartenbibliothek
 */
gc.gis.map.GCAbstractMap.prototype.removeLayer = gc.gis.abstractMethod;

/**
 * Gibt die Standard-Layer der Karte zurück.
 * @public
 * @abstract
 * @method
 * @return {L[]}
 */
gc.gis.map.GCAbstractMap.prototype.getLayers = gc.gis.abstractMethod;

/**
 * Setzt die Hintergrundkartenebene der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {L} layer Layerinstanz der Kartenbibliothek
 */
gc.gis.map.GCAbstractMap.prototype.setBackgroundLayer = gc.gis.abstractMethod;

/**
 * Setzt die aktuelle Hintergrundkartenebene der Karte zurück.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.unsetBackgroundLayer = gc.gis.abstractMethod;

/**
 * Löscht die Hintergrundkartenebene der Karte, sofern es sich um die aktuelle Hintergrundkartenebene handelt.
 *
 * @public
 * @abstract
 * @method
 * @param {L} layer Layerinstanz der Kartenbibliothek
 */
gc.gis.map.GCAbstractMap.prototype.removeBackgroundLayer = gc.gis.abstractMethod;

/**
 * Veranlasst das Neuladen der Layer der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {boolean} [opt_image=true] Flag Image-Layer neuladen
 * @param {boolean} [opt_tile=true] Flag Tile-Layer neuladen
 * @param {boolean} [opt_vector=true] Flag Vector-Layer neuladen
 */
gc.gis.map.GCAbstractMap.prototype.reloadLayers = gc.gis.abstractMethod;

/**
 * Fügt einen Tracking-Layer der Karte hinzu.
 *
 * @public
 * @abstract
 * @method
 * @param {gc.gis.layer.GCTrackingLayer} layer Hinzuzufügende Tracking-Layerinstanz der Kartenbibliothek
 * @fires gc.gis.map.GCMapEvent#trackinglayer:add
 */
gc.gis.map.GCAbstractMap.prototype.addTrackingLayer = gc.gis.abstractMethod;

/**
 * Entfernt einen Tracking-Layer aus der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {gc.gis.layer.GCTrackingLayer} layer zu entfernende Tracking-Layerinstanz der Kartenbibliothek
 * @fires gc.gis.map.GCMapEvent#trackinglayer:remove
 */
gc.gis.map.GCAbstractMap.prototype.removeTrackingLayer = gc.gis.abstractMethod;

/**
 * Entfernt alle Tracking-Layer der Karte.
 *
 * @public
 * @abstract
 * @method
 * @fires gc.gis.map.GCMapEvent#trackinglayer:removeall
 */
gc.gis.map.GCAbstractMap.prototype.removeAllTrackingLayers = gc.gis.abstractMethod;

/**
 * Liefert die Anzahl der Features aller aktuell sichtbaren Tracking-Layer der Karte.
 *
 * @public
 * @abstract
 * @method
 * @return {int}
 */
gc.gis.map.GCAbstractMap.prototype.numTrackingLayersFeatures = gc.gis.abstractMethod;

/**
 * Liefert den speziellen Tracking-Layer, der für das Highlighting verantwortlich ist.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.layer.GCTrackingLayer} Highlight-Layer
 */
gc.gis.map.GCAbstractMap.prototype.getHighlightLayer = gc.gis.abstractMethod;

/**
 * Zoom-Level der Karte setzen.
 *
 * @public
 * @abstract
 * @method
 * @param {number} Zoom-Level
 */
gc.gis.map.GCAbstractMap.prototype.zoomTo = gc.gis.abstractMethod;

/**
 * Kartenzentrum zur gegebenen Koordinate verschieben.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.panTo = gc.gis.abstractMethod;


/**
 * Passt den Kartenausschnitt an die angegebene Geometrie oder das angegebene Extent an.
 *
 * @public
 * @abstract
 * @method
 * @param {gc.gis.geom.GCGeometry|Array.<number>|string} geometryOrExtent Geometry, Extent ([minx, miny, maxx, maxy]) oder WKT-String
 * @param {Object} [opt_options] optionale Aufrufoptionen
 * @param {string} [opt_options.projection] aktuelle Kartenprojektion
 * @param {boolean} [opt_options.constrain_resolution=false] auf vorgegebene Auflösungen einschränken (Standard ist false)
 * @param {int} [opt_options.minscale] Maßstabstahl, die beim Zoom nicht unterschritten werden darf (z.B. 1000)
 */
gc.gis.map.GCAbstractMap.prototype.fit = gc.gis.abstractMethod;

/**
 * Gibt den Rotationswinkel der Karte zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {number} Rotationswinkel der Karte in Grad
 */
gc.gis.map.GCAbstractMap.prototype.getRotation = gc.gis.abstractMethod;

/**
 * Setzt den Rotationswinkel der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {number} rotation
 */
gc.gis.map.GCAbstractMap.prototype.setRotation = gc.gis.abstractMethod;

/**
 * Gibt die aktuelle Kartenprojektion zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {string} Aktuelle Kartenprojektion
 */
gc.gis.map.GCAbstractMap.prototype.getProjection = gc.gis.abstractMethod;

/**
 * Ändert die Kartenprojektion.
 *
 * @public
 * @abstract
 * @method
 * @param {string} projection Identifier der zu setzenden Kartenprojektion
 */
gc.gis.map.GCAbstractMap.prototype.setProjection = gc.gis.abstractMethod;

/**
 * Gibt den Mittelpunkt des aktuellen Kartenausschnitts in der aktuellen
 * Kartenprojektion zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {number} Mittelpunkt des aktuellen Kartenausschnitts
 */
gc.gis.map.GCAbstractMap.prototype.getCenter = gc.gis.abstractMethod;

/**
 * Ersetzt den Mittelpunkt des Kartenausschnitts.
 *
 * @public
 * @abstract
 * @method
 * @param {number} center Zu setzender Mittelpunkt des Kartenausschnitts
 */
gc.gis.map.GCAbstractMap.prototype.setCenter = gc.gis.abstractMethod;

/**
 * Gibt das aktuelle Zoom-Level der Karte zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {number} aktuelles Zoom-Level der Karte
 */
gc.gis.map.GCAbstractMap.prototype.getZoom = gc.gis.abstractMethod;

/**
 * Zoomt zum nächst höheren Zoom-Level.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.zoomIn = gc.gis.abstractMethod;

/**
 * Zoomt zum nächst niedrigeren Zoom-Level.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.zoomOut = gc.gis.abstractMethod;

/**
 * Gibt die aktuelle Maßstabszahl der Karte zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {number} aktuelle Maßstabszahl der Karte
 */
gc.gis.map.GCAbstractMap.prototype.getScale = gc.gis.abstractMethod;

/**
 * Setzt die Maßstabszahl der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {number} scale Maßstabszahl
 */
gc.gis.map.GCAbstractMap.prototype.setScale = gc.gis.abstractMethod;

/**
 * Gibt den Extent des aktuellen Kartenausschnitts zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.map.GCMapExtent}
 */
gc.gis.map.GCAbstractMap.prototype.getViewportExtent = gc.gis.abstractMethod;

/**
 * Gibt eine Objektrepräsentation des aktuellen Kartenausschnitts zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.map.GCMapViewport}
 */
gc.gis.map.GCAbstractMap.prototype.getViewport = gc.gis.abstractMethod;

/**
 * Setzt den Kartenausschnitt der Karte.
 *
 * @public
 * @abstract
 * @method
 * @param {gc.gis.map.GCMapViewport} viewport zu setzender Kartenausschnitt
 * @param {boolean} [animate=true] Flag, ob der Viewport mit Animation geändert werden soll
 */
gc.gis.map.GCAbstractMap.prototype.setViewport = gc.gis.abstractMethod;

/**
 * Transformiert einen Extent zwischen Koordinatenreferenzsystemen.
 *
 * @public
 * @param {gc.gis.map.GCMapExtent} extent Ursprungsextent
 * @param {string} source_crs Startkoordinatenreferenzsystem
 * @param {string} target_crs Zielkoordinatenreferenzsystem
 * @return {gc.gis.map.GCMapExtent} Extent im Zielkoordinatenreferenzsystem
 */
gc.gis.map.GCAbstractMap.prototype.transformExtent = gc.gis.abstractMethod;

/**
 * Zeigt einen Marker auf der Karte an.
 *
 * @public
 * @abstract
 * @method
 * @param {string} message Markertext
 * @param {number[]} position Markerposition
 * @param {Object} [opt_options] optionale Optionen
 * @param {string} [opt_options.projection] Optionale Projektion der übergebenen Koordinaten.
 *      Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {function} [opt_options.close] Callback, die beim Schließen des Markers aufgerufen wird
 * @param {function} [opt_options.enter] Callback, die beim Betreten des Markers mit der Maus aufgerufen wird
 * @param {function} [opt_options.leave] Callback, die beim Verlassen des Markers mit der Maus aufgerufen wird
 * @return {jQuery} Referenz auf das Element, welche als Marker erzeugt wurde
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCAbstractMap.prototype.showMarker = gc.gis.abstractMethod;

/**
 * Zeigt eine Vektorgeometrie auf der Karte an.
 *
 * @public
 * @abstract
 * @method
 * @param {*} geometry Geometrie
 * @param {string} format Formatstring der Geometrie
 * @param {Object} [opt_options] Optionale Optionen
 * @param {string} [opt_options.projection] Projektion der übergebenen Geometrie
 *                                          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {boolean} [opt_options.clear=false] Flag, ob bereits vorhandene Hervorhebungen gelöscht werden sollen
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCAbstractMap.prototype.highlightGeometry = gc.gis.abstractMethod;

/**
 * Zeigt eine Vektorgeometrie auf der Karte inkl. eines Markers an.
 *
 * @public
 * @abstract
 * @method
 * @param {*} geometry Geometrie
 * @param {string} format Formatstring der Geometrie
 * @param {string} message Markertext
 * @param {Object} [opt_options] Optionale Optionen
 * @param {string} [opt_options.projection] Projektion der übergebenen Geometrie
 *          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @param {boolean} [opt_options.clear=true] Flag, ob bereits vorhandene Hervorhebungen gelöscht werden sollen
 * @param {number[]} [opt_options.markerPosition] Markerposition
 * @param {number[]} [opt_options.markerProjection] Projektion der übergebenen Markerposition
 *          Default ist {@link gc.gis.map.GCMapView#getProjection}
 * @throws {gc.gis.error.GCOutOfMaxExtentError}
 */
gc.gis.map.GCAbstractMap.prototype.highlightGeometryWithMarker = gc.gis.abstractMethod;

/**
 * Setzt die aktuellen Hervorhebungen zurück.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.clearHighlighting = gc.gis.abstractMethod;

/**
 * Zeigt einen Tooltip am Mauszeiger innerhalb der Karte an.
 *
 * @public
 * @abstract
 * @method
 * @param {string} text Tooltiptext
 */
gc.gis.map.GCAbstractMap.prototype.showMouseTooltip = gc.gis.abstractMethod;

/**
 * Blendet den aktuell angezeigten Tooltip am Mauszeiger aus.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.disableMouseTooltip = gc.gis.abstractMethod;

/**
 * Prüft, ob die übergebene Geometrie mit dem eingestellten max. Extent eine Überschneidung hat. Sofern kein max. Extent
 * eingestellt ist, wird stets <code>true</code> zurückgegeben.
 *
 * @public
 * @abstract
 * @param {gc.gis.geom.GCGeometry} geometry
 * @return {boolean}
 */
gc.gis.map.GCAbstractMap.prototype.intersectsMaxExtent = gc.gis.abstractMethod;

/**
 * Wandelt Pixelkoordinaten in Weltkoordinaten der aktuellen Projektion um.
 *
 * @public
 * @abstract
 * @method
 * @param {number} pixel
 * @return {number}
 */
gc.gis.map.GCAbstractMap.prototype.pixelToWorld = gc.gis.abstractMethod;

/**
 * Wandelt Weltkoordinaten der aktuellen Projektion in Pixelkoordinaten um.
 *
 * @public
 * @abstract
 * @method
 * @param {number} coordinate
 * @return {number}
 */
gc.gis.map.GCAbstractMap.prototype.worldToPixel = gc.gis.abstractMethod;

/**
 * Gibt an, ob das Koordinatenreferenzsystem unterstützt wird.
 *
 * @public
 * @abstract
 * @method
 * @param {string} crsName Name der Projektion z.B. (EPSG:4326)
 * @return {boolean}
 */
gc.gis.map.GCAbstractMap.prototype.supportsCRS = gc.gis.abstractMethod;

/**
 * Gibt die Größe der Karte (Breite/Höhe) im DOM in Pixel zurück.
 *
 * @public
 * @abstract
 * @method
 * @return {gc.gis.map.GCMapSize} Breite/Höhe der Karte in Pixeln
 */
gc.gis.map.GCAbstractMap.prototype.getSize = gc.gis.abstractMethod;

/**
 * Aktualisiert die Karte.
 *
 * @public
 * @abstract
 * @method
 */
gc.gis.map.GCAbstractMap.prototype.updateSize = gc.gis.abstractMethod;

/**
 * Gibt die Data-URL eines Canvas-Elements unterhalb von {@link gc.gis.map.GCMapView#target} mit den Inhalten der
 * Tracking-Layer zurück (Screenshot).
 *
 * @public
 * @abstract
 * @method
 * @param {string} format Ausgabeformat (image/png oder image/jpeg)
 * @return {JQueryPromise<T>} ein jQuery Deferred's Promise object {@link http://api.jquery.com/Types/#Promise}
 */
gc.gis.map.GCAbstractMap.prototype.dumpCanvasTrackingLayer = gc.gis.abstractMethod;

/**
 * Gibt die Data-URL eines Canvas-Elements innerhalb einer Bounding-Box und in der gewünschten Größe unterhalb mit den
 * Inhalten der Tracking-Layer zurück (Screenshot).
 *
 * @public
 * @abstract
 * @method
 * @param {gc.gis.map.GCMapExtent} box Boundingbox
 * @param {Number[]} size Breite und Höhe des Screenshots z.B. [200, 100]
 * @param {string} format Ausgabeformat (image/png oder image/jpeg)
 * @return {JQueryPromise<T>} ein jQuery Deferred's Promise object {@link http://api.jquery.com/Types/#Promise}
 */
gc.gis.map.GCAbstractMap.prototype.dumpCanvasTrackingLayerByBox = gc.gis.abstractMethod;